import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

export const SERVICE_TYPES = [
  'Reagendamento de Biometria',
  'Reagendamento de Entrevista',
  'Mudança de Endereço',
  'Relatório Psicológico',
  'Traduções',
  'Outro',
] as const;

export const SERVICE_STATUSES = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
] as const;

export const caseServiceSchema = z.object({
  service_type: z.string().min(1, 'Selecione o serviço'),
  description: z.string().max(500).optional().nullable(),
  service_date: z.string().optional().nullable(),
  due_date: z.string().optional().nullable(),
  status: z.string().min(1),
  assigned_to: z.string().uuid().optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
});

export type CaseServiceInput = z.infer<typeof caseServiceSchema>;

export interface CaseService extends CaseServiceInput {
  id: string;
  case_id: string;
  created_at: string;
  assignee?: { id: string; first_name: string | null; last_name: string | null } | null;
}

export function useCaseServices(caseId: string | null) {
  const queryClient = useQueryClient();
  const queryKey = ['case-services', caseId];

  const query = useQuery({
    queryKey,
    enabled: !!caseId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_services')
        .select(`
          id, case_id, service_type, description, service_date, due_date,
          status, assigned_to, notes, created_at,
          assignee:app_users!case_services_assigned_to_fkey(id, first_name, last_name)
        `)
        .eq('case_id', caseId!)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as CaseService[];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createService = useMutation({
    mutationFn: async (input: CaseServiceInput) => {
      const parsed = caseServiceSchema.parse(input);
      const { error } = await supabase.from('case_services').insert([{
        ...parsed,
        case_id: caseId,
        service_date: parsed.service_date || null,
        due_date: parsed.due_date || null,
        assigned_to: parsed.assigned_to || null,
      }] as any);

      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateService = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CaseServiceInput> }) => {
      const { error } = await supabase
        .from('case_services')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteService = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('case_services').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    services: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createService: createService.mutateAsync,
    isCreating: createService.isPending,
    updateService: updateService.mutateAsync,
    deleteService: deleteService.mutateAsync,
  };
}
